import { MapPin, Phone, Mail, Clock, ArrowUpRight } from "lucide-react";
import { BRAND } from "@/lib/site";
import { Button } from "@/components/ui/button";

const SD_IMG =
  "https://images.unsplash.com/photo-1514939775307-d44e7f10cabd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwxfHxTYW4lMjBEaWVnbyUyMHNreWxpbmV8ZW58MHx8fHwxNzc5MTExMDE2fDA&ixlib=rb-4.1.0&q=85";

const MAP_EMBED =
  "https://www.google.com/maps?q=2509+El+Cajon+Blvd+%23A%2C+San+Diego%2C+CA+92104&output=embed";

export default function Contact() {
  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 sm:py-32 bg-slate-900 text-white overflow-hidden"
    >
      <img
        src={SD_IMG}
        alt="San Diego skyline"
        className="absolute inset-0 h-full w-full object-cover opacity-25"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-slate-900/85 via-slate-900/90 to-slate-900" />

      <div className="relative max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-6">
          <div className="text-xs font-bold uppercase tracking-[0.22em] text-blue-400">
            Visit Us
          </div>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.05]">
            We're right on
            <br />
            El Cajon Boulevard.
          </h2>
          <p className="mt-6 text-lg text-white/70 max-w-xl leading-relaxed">
            Free street parking, walk-ins welcome, and a quick stop for most
            services. Call ahead and we'll have your paperwork ready when you arrive.
          </p>

          <div className="mt-10 grid sm:grid-cols-2 gap-4">
            <InfoCard icon={MapPin} label="Address" value={BRAND.address} />
            <InfoCard icon={Phone} label="Phone" value={BRAND.phone} href={BRAND.phoneHref} />
            <InfoCard icon={Mail} label="Email" value={BRAND.email} href={BRAND.emailHref} />
            <InfoCard
              icon={Clock}
              label="Hours"
              value="Mon–Fri 9–6 · Sat 10–1"
            />
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <Button
              asChild
              data-testid="contact-directions-button"
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg h-12 px-6 font-semibold"
            >
              <a
                href={BRAND.mapsUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2"
              >
                Get Directions <ArrowUpRight className="h-4 w-4" />
              </a>
            </Button>
            <Button
              asChild
              variant="outline"
              data-testid="contact-book-button"
              className="bg-transparent border-white/20 text-white hover:bg-white/10 hover:text-white rounded-lg h-12 px-6 font-semibold"
            >
              <a href="#book">Book Online</a>
            </Button>
          </div>
        </div>

        <div className="lg:col-span-6">
          <div
            data-testid="contact-map"
            className="aspect-[4/3] lg:aspect-square rounded-3xl overflow-hidden border border-white/10 shadow-xl bg-slate-800"
          >
            <iframe
              title="Virtue Registration Services location"
              src={MAP_EMBED}
              className="h-full w-full"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function InfoCard({ icon: Icon, label, value, href }) {
  const content = (
    <>
      <div className="h-10 w-10 rounded-xl bg-white/10 border border-white/15 grid place-items-center shrink-0">
        <Icon className="h-5 w-5 text-blue-300" />
      </div>
      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-white/60 font-semibold">
          {label}
        </div>
        <div className="mt-1 font-display text-[15px] sm:text-base font-semibold text-white">
          {value}
        </div>
      </div>
    </>
  );

  const base =
    "flex items-start gap-4 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-md p-5 hover:bg-white/10 transition-colors";

  return href ? (
    <a href={href} className={base}>
      {content}
    </a>
  ) : (
    <div className={base}>{content}</div>
  );
}
